/* eslint-disable react-hooks/rules-of-hooks */
import * as React from "react";
import { cn } from "../lib/utils";

interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  errorText?: string;
  helperText?: string;
  label?: string;
  topLabel?: string;
  variant?: "standard" | "outlined" | "filled";
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      variant = "outlined",
      error = false,
      errorText,
      helperText,
      label,
      topLabel,
      id,
      defaultValue,
      value,
      onChange,
      placeholder,
      ...props
    },
    ref
  ) => {
    const textareaId = id || React.useId();
    const describedById = errorText ? `${textareaId}-error-text` : undefined;

    const [focused, setFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(!!defaultValue || !!value);
    const [keyboardFocus, setKeyboardFocus] = React.useState(false);
    const [lastInteractionWasKeyboard, setLastInteractionWasKeyboard] = React.useState(false);

    React.useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          setLastInteractionWasKeyboard(true);
        }
      };

      const handleMouseDown = () => {
        setLastInteractionWasKeyboard(false);
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('mousedown', handleMouseDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('mousedown', handleMouseDown);
      };
    }, []);

    const handleFocus = () => {
      setFocused(true);
      setKeyboardFocus(lastInteractionWasKeyboard);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocused(false);
      setKeyboardFocus(false);
      setHasValue(!!e.target.value);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setHasValue(!!e.target.value);
      onChange?.(e);
    };

    const variantClasses = {
      outlined: "bg-background border border-input dark:border-input/40 rounded-sm",
      filled: "border border-input dark:border-input/40 rounded-sm bg-muted/30 hover:bg-muted/50 dark:bg-muted/10 dark:hover:bg-muted/20",
      standard: "border-0 border-b border-input dark:border-input/40 bg-transparent focus-visible:ring-0 shadow-none",
    };

    const baseBorderClass = error
      ? "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20"
      : "border-input dark:border-input/40";

    const getHoverFocusBorderClass = () => {
      if (variant === "standard") {
        return error
          ? "hover:border-b-destructive hover:border-b-[1px] focus:border-b-destructive focus:border-b-[1px]"
          : "hover:border-b-primary hover:border-b-[1px] focus:border-b-primary focus:border-b-[1px]";
      } else {
        return error
          ? "hover:border-destructive hover:border-[1px] focus:border-destructive focus:border-[1px]"
          : "hover:border-primary hover:border-[1px] focus:border-primary focus:border-[1px]";
      }
    };

    const borderClass = `${baseBorderClass} ${getHoverFocusBorderClass()}`;
    const shouldFloatLabel = focused || hasValue;

    return (
      <div className="relative w-full space-y-1.5">
        {topLabel && (
          <div className="text-foreground mb-2 text-sm font-medium">
            {topLabel}
          </div>
        )}

        <div className={cn("relative", label && "pb-2")}>
          {label && (
            <label
              htmlFor={textareaId}
              className={cn(
                "absolute left-3 transition-all duration-200 z-10 px-1 bg-background dark:bg-background", error ? "text-destructive" : "text-muted-foreground",
                "pointer-events-none",
                shouldFloatLabel ? "-top-2 text-xs" : "top-2.5 text-sm"
              )}
            >
              {label}
            </label>
          )}

          <textarea
            id={textareaId}
            ref={ref}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            value={value}
            defaultValue={defaultValue}
            aria-invalid={error}
            aria-describedby={describedById}
            placeholder={label ? " " : placeholder}
            className={cn(
              "peer selection:bg-primary selection:text-primary-foreground flex w-full min-w-0 min-h-[90px] resize-y text-base shadow-xs transition-all duration-200 outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground",
              variantClasses[variant],
              borderClass,
              "px-3 py-2",

              // Regular focus styles (mouse click)
              !error && focused && !keyboardFocus && variant !== "standard" && "ring-[1px] ring-primary",
              !error && focused && !keyboardFocus && variant === "standard" && "border-b-[1px] border-b-primary",
              error && focused && !keyboardFocus && variant !== "standard" && "ring-[0px] ring-destructive",
              error && focused && !keyboardFocus && variant === "standard" && "border-b-[1px] border-b-destructive",
              
              // Keyboard focus styles (tab key)
              !error && keyboardFocus && variant !== "standard" && "ring-[3px] ring-primary/20",
              !error && keyboardFocus && variant === "standard" && "!border !border-primary !rounded-sm !ring-[3px] !ring-primary/20",
              error && keyboardFocus && variant !== "standard" && "ring-[3px] ring-destructive/20",
              error && keyboardFocus && variant === "standard" && "!border !border-destructive !rounded-sm !ring-[3px] !ring-destructive/20",

              label && "placeholder-transparent pt-5",
              error && "aria-invalid:ring-error/20 dark:aria-invalid:ring-error/40",
              className
            )}
            {...props}
          />
        </div>

        {errorText && (
          <p id={describedById} className="text-xs text-destructive mt-1">
            {errorText}
          </p>
        )}

        {!errorText && helperText && (
          <p className="text-xs text-muted-foreground mt-1">{helperText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";
export { Textarea };
